import { BaseEntityAdapter } from './base/EntityAdapter';
import type { Transaction } from '@shared/types/payment.types';

export interface PaymentDetail {
  label: string;
  value: string;
  type?: 'text' | 'currency' | 'date' | 'status';
}

export class PaymentAdapter extends BaseEntityAdapter<Transaction, PaymentDetail[]> {
  private stateMap: Record<number, string> = {
    1: 'Approved',
    2: 'Declined',
    3: 'Pending',
    4: 'Captured',
    5: 'Voided'
  };

  adapt(transaction: Transaction): PaymentDetail[] {
    const details: PaymentDetail[] = [];

    // Transaction Reference
    if (transaction.transactionRef && transaction.transactionRef.trim() !== '') {
      details.push({
        label: 'Transaction Reference',
        value: transaction.transactionRef,
        type: 'text'
      });
    }

    // Amount
    if (transaction.amounts?.original) {
      const amount = transaction.amounts.original;
      const formattedAmount = (amount.amount / Math.pow(10, amount.decimal)).toFixed(amount.decimal);
      details.push({
        label: 'Amount',
        value: `${formattedAmount} ${amount.currency}`,
        type: 'currency'
      });
    }

    // State
    if (transaction.state !== null && transaction.state !== undefined) {
      details.push({
        label: 'Status',
        value: this.stateMap[transaction.state as number] || `State ${transaction.state}`,
        type: 'status'
      });
    }

    // Scheme
    if (transaction.scheme && transaction.scheme.trim() !== '') {
      details.push({
        label: 'Card Scheme',
        value: transaction.scheme,
        type: 'text'
      });
    }

    // PAN Last 4
    if (transaction.panLast4 && transaction.panLast4.trim() !== '') {
      details.push({
        label: 'Card Number',
        value: `****${transaction.panLast4}`,
        type: 'text'
      });
    }

    // RRN
    if (transaction.rrn && transaction.rrn.trim() !== '') {
      details.push({
        label: 'RRN',
        value: transaction.rrn,
        type: 'text'
      });
    }

    // Terminal ID
    if (transaction.terminalId && transaction.terminalId.trim() !== '') {
      details.push({
        label: 'Terminal ID',
        value: transaction.terminalId,
        type: 'text'
      });
    }

    // Auth Code
    if (transaction.authIdentCode && transaction.authIdentCode.trim() !== '') {
      details.push({
        label: 'Auth Code',
        value: transaction.authIdentCode,
        type: 'text'
      });
    }

    // Response Code
    if (transaction.responseCode && transaction.responseCode.trim() !== '') {
      details.push({
        label: 'Response Code',
        value: transaction.responseCode,
        type: 'text'
      });
    }

    // Timestamps
    if (transaction.timestamps?.initiated && transaction.timestamps.initiated.trim() !== '') {
      details.push({
        label: 'Initiated',
        value: new Date(transaction.timestamps.initiated).toLocaleString(),
        type: 'date'
      });
    }

    if (transaction.timestamps?.authorized && transaction.timestamps.authorized.trim() !== '') {
      details.push({
        label: 'Authorized',
        value: new Date(transaction.timestamps.authorized).toLocaleString(),
        type: 'date'
      });
    }

    // Account
    if (transaction.account?.name && transaction.account.name.trim() !== '') {
      details.push({
        label: 'Account',
        value: transaction.account.name,
        type: 'text'
      });
    }

    // Risk Score
    if (transaction.riskScore !== null && transaction.riskScore !== undefined) {
      details.push({
        label: 'Risk Score',
        value: transaction.riskScore.toString(),
        type: 'text'
      });
    }

    // 3DS Status
    if (transaction.threeDSStatusCode && transaction.threeDSStatusCode.trim() !== '') {
      details.push({
        label: '3DS Status',
        value: transaction.threeDSStatusCode,
        type: 'text'
      });
    }

    return details;
  }
}